import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { CARD_FIELDS, CARD_TYPES } from '../../../../constants/card'
import Input from '../../../Common/Input'
import { useTranslations } from '../../../../hooks/useTranslations'
import { AUTOCOMPLETE_VALUES } from '../../../../constants/autocompleteValues'
import { getSelectionText } from '../../../../helpers/common'
import classNames from 'classnames'
import styles from './CVV.module.scss'

const CVV = ({
  register,
  errors,
  trigger,
  setValue,
  cardType,
  label,
  cardBrand,
}) => {
  const l10n = useTranslations()

  const CVV_LABELS = {
    Visa: '3 digits printed on the signature strip',
    MasterCard: '3 digits printed on the signature strip',
    AmEx: '4 digits on the front of your card',
  }

  const inputLabel = label || l10n.t('Security code')

  let pattern
  let maxLength
  switch (cardType) {
    case CARD_TYPES.amex: {
      pattern = /^\d{4}$/
      maxLength = 4
      break
    }
    case '':
    case CARD_TYPES.omnio: {
      pattern = /^\d{3,4}$/
      maxLength = 4
      break
    }
    default: {
      pattern = /^\d{3}$/
      maxLength = 3
      break
    }
  }

  const reset = () => setValue(CARD_FIELDS.cvv, '', { shouldValidate: true })

  const handleKeyPress = async e => {
    const value = e?.target?.value
    const lengthValidation =
      value?.length >= maxLength && getSelectionText().length !== value.length
    if (!/\d/.test(e.key) || lengthValidation) {
      e.preventDefault()
      await trigger(CARD_FIELDS.cvv)
    }
  }

  const cvvStyles = classNames(styles.cvvInput, 'tenant-overrides-cvv')

  return (
    <div className={styles.cvv}>
      <Input
        label={
          <>
            {inputLabel}
            <span data-testid="cvv-label" className={styles.label}>
              {l10n.t(
                CVV_LABELS[cardBrand] && !errors.number
                  ? CVV_LABELS[cardBrand]
                  : '3 or 4 digits printed on your card',
              )}
            </span>
          </>
        }
        inputProps={{
          'data-apexx': 'cvv',
          'data-testid': 'cvv',
          name: 'cvv',
          type: 'password',
          id: CARD_FIELDS.cvv,
          onKeyPress: handleKeyPress,
          className: cvvStyles,
          inputmode: 'numeric',
          autocomplete: AUTOCOMPLETE_VALUES.CARD_CVV,
          ...register(CARD_FIELDS.cvv, {
            required: cardType !== CARD_TYPES.maestro,
            pattern,
          }),
        }}
        reset={reset}
        hasError={Boolean(errors[CARD_FIELDS.cvv])}
        errorMessage={l10n.t(
          'Please enter the security code as it appears on the card',
        )}
      />
    </div>
  )
}

CVV.propTypes = {
  label: PropTypes.string,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  cardType: PropTypes.oneOf([...Object.values(CARD_TYPES), '']),
}

export default CVV
